import React from "react";
import { Card, Row, Col, Button, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { createAlert } from "../../../../redux/reducers/Alerts";
import TrackedButton from "../../../Docuclipper/TrackedButton";
import Share from "./share";

const FeatureCard: React.FC<{
  icon: any;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Body className="d-flex flex-column align-items-center text-center p-4">
      <div className="mb-3">
        <FontAwesomeIcon icon={icon} size="2x" className="text-primary" />
      </div>
      <h4 className="mb-2">{title}</h4>
      <p className="text-muted mb-0">{description}</p>
    </Card.Body>
  </Card>
);

export const ReferralProgram: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: ReduxState) => state.User);

  if (!user) return null;

  const referralLink = `https://www.docuclipper.com/signup?utm_medium=referral&utm_source=${user.referralCode}`;

  return (
    <Container>
      {/* Hero Section */}
      <Row className="align-items-center mb-5">
        <Col lg={8} className="mx-auto text-center">
          <h1 className="display-4 mb-4">Earn 20% Commission</h1>
          <p className="lead text-muted mb-5">
            Join our affiliate program and earn 20% commission on your
            referrals' first year subscription. The more customers you refer,
            the more you earn!
          </p>
          <a
            href="https://docuclipper.firstpromoter.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-lg"
          >
            Start Earning Now
          </a>
        </Col>
      </Row>

      {/* Features Section */}
      <Row className="mb-5 g-4">
        <Col md={4}>
          <FeatureCard
            icon="dollar-sign"
            title="Generous Commission"
            description="Earn 20% of your referrals' subscription fees for their entire first year"
          />
        </Col>
        <Col md={4}>
          <FeatureCard
            icon="chart-line"
            title="Unlimited Earnings"
            description="No cap on referrals or earnings - the more you refer, the more you make"
          />
        </Col>
        <Col md={4}>
          <FeatureCard
            icon="clock"
            title="Quick Payouts"
            description="Get paid monthly for all qualifying referrals"
          />
        </Col>
      </Row>

      {/* Review Section */}
      <Row className="mb-5">
        <Col lg={8} className="mx-auto text-center">
          <h2 className="mb-4">Get 100 Free Pages ($60 value)</h2>
          <div className="mb-4">
            <FontAwesomeIcon icon="star" className="text-warning" size="2x" />
            <FontAwesomeIcon
              icon="star"
              className="text-warning mx-2"
              size="2x"
            />
            <FontAwesomeIcon icon="star" className="text-warning" size="2x" />
            <FontAwesomeIcon
              icon="star"
              className="text-warning mx-2"
              size="2x"
            />
            <FontAwesomeIcon icon="star" className="text-warning" size="2x" />
          </div>
          <p className="lead mb-4" style={{ color: "#2c3e50" }}>
            Leave us a review and get 100 free pages (valued at $60)!
          </p>
          <div
            className="p-4 rounded"
            style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6" }}
          >
            <h5 className="mb-3" style={{ color: "#2c3e50" }}>
              How to get your free pages:
            </h5>
            <ol className="text-left" style={{ color: "#2c3e50" }}>
              <li className="mb-2">
                Visit either:
                <ul className="mt-2">
                  <li className="mb-2">
                    <strong>QuickBooks Online users:</strong>{" "}
                    <a
                      href="https://quickbooks.intuit.com/app/apps/appdetails/docuclipper/en-us/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#007bff", textDecoration: "underline" }}
                    >
                      DocuClipper on Intuit App Store
                    </a>
                  </li>
                  <li>
                    <strong>All users:</strong>{" "}
                    <a
                      href="https://www.g2.com/products/docuclipper/reviews"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#007bff", textDecoration: "underline" }}
                    >
                      DocuClipper on G2
                    </a>
                  </li>
                </ul>
              </li>
              <li className="mb-2">
                Leave a review about your experience with DocuClipper
              </li>
              <li className="mb-2">Take a screenshot of your review</li>
              <li>
                Email the screenshot to{" "}
                <a
                  href="mailto:support@docuclipper.com"
                  style={{ color: "#007bff", textDecoration: "underline" }}
                >
                  support@docuclipper.com
                </a>{" "}
                and we'll add 100 free pages to your account
              </li>
            </ol>
            <p
              style={{
                color: "#495057",
                marginTop: "1.5rem",
                marginBottom: "0",
              }}
            >
              Need inspiration? Check out{" "}
              <a
                href="https://docuclipper.com/reviews"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#007bff", textDecoration: "underline" }}
              >
                what other users are saying
              </a>{" "}
              about DocuClipper.
            </p>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ReferralProgram;
